

import React, { createContext, useState, useCallback, useEffect } from "react";

export const UserContext = createContext({
  user: null,
  userType: "client", // ✅ "client" or "student"
  setUser: () => {},
  setUserType: () => {},
  loading: true,
  refreshUser: () => Promise.resolve(),
  logout: () => {},
});

export function UserProvider({ children }) {
  const [user, setUserState] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [userType, setUserTypeState] = useState(() => {
    try {
      return localStorage.getItem("userType") || "client";
    } catch {
      return "client";
    }
  });

  const [loading, setLoading] = useState(true);

  // Helper to update state and localStorage together
  const setUser = useCallback((u) => {
    setUserState(u);
    try {
      if (u) localStorage.setItem("user", JSON.stringify(u));
      else localStorage.removeItem("user");
    } catch (e) {
      // ignore
    }
  }, []);

  const setUserType = useCallback((type) => {
    setUserTypeState(type);
    try {
      localStorage.setItem("userType", type);
    } catch (e) {
      // ignore
    }
  }, []);

  const refreshUser = useCallback(async (overrideUserType = null) => {
    try {
      // Use the provided userType override, or fall back to current state
      //const effectiveUserType = overrideUserType !== null ? overrideUserType : userType;
      
      // Try to fetch from the general /auth/me endpoint first (works after Google OAuth)
      let res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
        credentials: "include",
      });

      // If /auth/me succeeds, user is a client (even if we thought they were student)
      if (res.ok) {
        const data = await res.json();
        const fetchedUser = data.user || null;
        
        // Auto-detect and update userType based on response
        if (fetchedUser) {
          const detectedType = fetchedUser.role === "student" ? "student" : "client";
          if (detectedType !== userType) {
            setUserType(detectedType);
          }
        }
        
        setUser(fetchedUser);
        setLoading(false);
        return fetchedUser;
      }

      // If /auth/me fails, always try /student/me as fallback (handles Google OAuth students)
      res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/student/me`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        // Auto-detect and set userType to student
        if (userType !== "student") {
          setUserType("student");
        }
        setUser(data.user || null);
        setLoading(false);
        return data.user || null;
      }

      // Both endpoints failed, clear user
      setUser(null);
      setLoading(false);
      return null;
    } catch (err) {
      console.error("refreshUser error:", err);
      setUser(null);
      setLoading(false);
      return null;
    }
  }, [setUser, userType,setUserType]);

  const logout = useCallback(async () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout error", err);
    }
  }, [setUser]);

  useEffect(() => {
    let mounted = true;

    if (!user) {
      (async () => {
        await refreshUser();
      })();
    } else {
      if (mounted) setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [user, refreshUser]);

  return (
    <UserContext.Provider value={{ user, userType, setUser, setUserType, loading, refreshUser, logout }}>
      {children}
    </UserContext.Provider>
  );
}